"use client";

import type { useChat } from "@ai-sdk/react";
import { useLingui } from "@lingui/react";
import { Trans } from "@lingui/react/macro";
import { useQuery } from "@urql/next";
import { getToolName, isToolUIPart, type ToolUIPart } from "ai";
import { graphql } from "gql.tada";
import { OrderDirectionLabels } from "@/app/[lang]/[workspaceId]/sa/constants";
import {
  BrowserCategoryLabels,
  DefaultChannelGroupLabels,
  DeviceCategoryLabels,
  OSCategoryLabels,
  SessionPerformanceDetailGroupByLabels,
  SessionPerformanceDetailOrderByFieldLabels,
  UserAnalyticsDataDateTypeLabels,
  UserAnalyticsFilterLabels,
} from "@/app/[lang]/[workspaceId]/ua/constants";
import { Skeleton } from "@/components/ui/skeleton";
import type { AgentUIMessage } from "@/lib/ai/types";
import { ApproveButton } from "../ui/approve-button";
import { DenyButton } from "../ui/deny-button";
import type { CreateUserAnalyticsViewInput } from "./schema";

const ProjectDataQuery = graphql(`
  query UaViewConfirmProjectData($id: ID!) {
    node(id: $id) {
      __typename
      ... on UserAnalyticsProject {
        name
        userAnalytics {
          id
          __typename
          urlGroups {
            id
            __typename
            name
          }
        }
      }
    }
  }
`);

type CreateUaViewInput = CreateUserAnalyticsViewInput;

type Props = {
  part: ToolUIPart;
  addToolResult: ReturnType<typeof useChat<AgentUIMessage>>["addToolResult"];
  sendMessage: ReturnType<typeof useChat<AgentUIMessage>>["sendMessage"];
};

export function CreateUserAnalyticsViewConfirm({
  part,
  addToolResult,
  sendMessage,
}: Props) {
  const { i18n } = useLingui();
  const isTarget =
    isToolUIPart(part) && getToolName(part) === "create_user_analytics_view";
  const isInputAvailable = part.state === "input-available";

  const input = (part.input ?? {}) as Partial<CreateUaViewInput>;

  const projectId = input.projectId ?? "";

  // プロジェクト名やURLグループ名を表示するために追加情報を取得する
  const [{ data, fetching }] = useQuery({
    query: ProjectDataQuery,
    variables: { id: projectId },
    pause: !(isTarget && isInputAvailable && projectId),
  });

  const project =
    data?.node?.__typename === "UserAnalyticsProject" ? data.node : null;
  const projectName = project?.name ?? "Unknown";
  const urlGroups = project?.userAnalytics?.urlGroups ?? [];
  const urlGroupNames = Object.fromEntries(
    urlGroups.map((group) => [group.id, group.name]),
  );

  if (!isTarget || !isInputAvailable) return null;

  const detailGroupLabels =
    input.detailGroupBy?.map((group) =>
      i18n._(
        SessionPerformanceDetailGroupByLabels[
          group as keyof typeof SessionPerformanceDetailGroupByLabels
        ],
      ),
    ) ?? [];

  const detailOrderByLabel = input.detailOrderBy
    ? i18n._(
        SessionPerformanceDetailOrderByFieldLabels[
          input.detailOrderBy
            .field as keyof typeof SessionPerformanceDetailOrderByFieldLabels
        ],
      )
    : null;

  const detailOrderDirectionLabel = input.detailOrderBy
    ? i18n._(
        OrderDirectionLabels[
          input.detailOrderBy.direction as keyof typeof OrderDirectionLabels
        ],
      )
    : null;

  const campaignsSummary =
    input.campaigns?.map((campaign, index) => {
      const filter = input.campaignsFilterType?.[index];
      if (filter) {
        return `${campaign} (${i18n._(
          UserAnalyticsFilterLabels[
            filter as keyof typeof UserAnalyticsFilterLabels
          ],
        )})`;
      }
      return campaign;
    }) ?? [];

  const eventParamsSummary =
    input.eventParams?.map((eventParam) => {
      const operator = i18n._(
        UserAnalyticsFilterLabels[
          eventParam.operator as keyof typeof UserAnalyticsFilterLabels
        ],
      );
      return `${eventParam.key} ${operator} ${eventParam.value}`;
    }) ?? [];

  return (
    <div className="p-4 text-sm">
      <div className="mb-2 font-medium">
        <Trans>Do you want to create a new User Analytics view?</Trans>
      </div>
      <ul className="mb-4 list-disc space-y-1 pl-5">
        <li>
          <Trans>Name</Trans>: <span className="font-medium">{input.name}</span>
        </li>
        {input.description ? (
          <li>
            <Trans>Description</Trans>:{" "}
            <span className="font-normal">{input.description}</span>
          </li>
        ) : null}
        {projectId ? (
          <li>
            <Trans>Project</Trans>:{" "}
            {fetching ? (
              <Skeleton className="h-4 w-12" />
            ) : (
              <span className="font-medium">{projectName}</span>
            )}
          </li>
        ) : null}
        {input.defaultDateRange ? (
          <li>
            <Trans>Saved date</Trans>:{" "}
            <span className="font-medium">
              {i18n._(
                UserAnalyticsDataDateTypeLabels[
                  input.defaultDateRange
                    .type as keyof typeof UserAnalyticsDataDateTypeLabels
                ],
              )}
            </span>
            {input.defaultDateRange.type === "CUSTOM" &&
            input.defaultDateRange.dateRange ? (
              <span className="ml-2 text-muted-foreground">
                ({input.defaultDateRange.dateRange.start} -{" "}
                {input.defaultDateRange.dateRange.end})
              </span>
            ) : null}
          </li>
        ) : null}
        {input.visitedPage ? (
          <li>
            <Trans>Visited page</Trans>:{" "}
            <span className="font-medium break-all">{input.visitedPage}</span>
            {input.visitedPageFilterType ? (
              <span className="ml-2 text-muted-foreground">
                {i18n._(
                  UserAnalyticsFilterLabels[
                    input.visitedPageFilterType as keyof typeof UserAnalyticsFilterLabels
                  ],
                )}
              </span>
            ) : null}
          </li>
        ) : null}
        {input.userPseudoId ? (
          <li>
            <Trans>User ID</Trans>:{" "}
            <span className="font-medium break-all">{input.userPseudoId}</span>
            {input.userPseudoIdFilterType ? (
              <span className="ml-2 text-muted-foreground">
                {i18n._(
                  UserAnalyticsFilterLabels[
                    input.userPseudoIdFilterType as keyof typeof UserAnalyticsFilterLabels
                  ],
                )}
              </span>
            ) : null}
          </li>
        ) : null}
        {input.sessionId ? (
          <li>
            <Trans>Session ID</Trans>:{" "}
            <span className="font-medium break-all">{input.sessionId}</span>
            {input.sessionIdFilterType ? (
              <span className="ml-2 text-muted-foreground">
                {i18n._(
                  UserAnalyticsFilterLabels[
                    input.sessionIdFilterType as keyof typeof UserAnalyticsFilterLabels
                  ],
                )}
              </span>
            ) : null}
          </li>
        ) : null}
        {input.firstPageLocationURL ? (
          <li>
            <Trans>Landing page</Trans>:{" "}
            <span className="font-medium break-all">
              {input.firstPageLocationURL}
            </span>
            {input.firstPageLocationFilterType ? (
              <span className="ml-2 text-muted-foreground">
                {i18n._(
                  UserAnalyticsFilterLabels[
                    input.firstPageLocationFilterType as keyof typeof UserAnalyticsFilterLabels
                  ],
                )}
              </span>
            ) : null}
          </li>
        ) : null}
        {input.firstPageLocationURLGroupIDs &&
        input.firstPageLocationURLGroupIDs.length ? (
          <li>
            <Trans>Landing page URL group</Trans>:{" "}
            <span className="font-medium">
              {input.firstPageLocationURLGroupIDs
                .map((id) => urlGroupNames[id] || id)
                .join(", ")}
            </span>
          </li>
        ) : null}
        {input.firstPageTitle ? (
          <li>
            <Trans>Landing page title</Trans>:{" "}
            <span className="font-medium break-all">
              {input.firstPageTitle}
            </span>
            {input.firstPageTitleFilterType ? (
              <span className="ml-2 text-muted-foreground">
                {i18n._(
                  UserAnalyticsFilterLabels[
                    input.firstPageTitleFilterType as keyof typeof UserAnalyticsFilterLabels
                  ],
                )}
              </span>
            ) : null}
          </li>
        ) : null}
        {input.secondPageLocationURL ? (
          <li>
            <Trans>Second page</Trans>:{" "}
            <span className="font-medium break-all">
              {input.secondPageLocationURL}
            </span>
            {input.secondPageLocationFilterType ? (
              <span className="ml-2 text-muted-foreground">
                {i18n._(
                  UserAnalyticsFilterLabels[
                    input.secondPageLocationFilterType as keyof typeof UserAnalyticsFilterLabels
                  ],
                )}
              </span>
            ) : null}
          </li>
        ) : null}
        {input.secondPageLocationURLGroupIDs &&
        input.secondPageLocationURLGroupIDs.length ? (
          <li>
            <Trans>Second page URL group</Trans>:{" "}
            <span className="font-medium">
              {input.secondPageLocationURLGroupIDs
                .map((id) => urlGroupNames[id] || id)
                .join(", ")}
            </span>
          </li>
        ) : null}
        {input.secondPageTitle ? (
          <li>
            <Trans>Second page title</Trans>:{" "}
            <span className="font-medium break-all">
              {input.secondPageTitle}
            </span>
            {input.secondPageTitleFilterType ? (
              <span className="ml-2 text-muted-foreground">
                {i18n._(
                  UserAnalyticsFilterLabels[
                    input.secondPageTitleFilterType as keyof typeof UserAnalyticsFilterLabels
                  ],
                )}
              </span>
            ) : null}
          </li>
        ) : null}
        {input.devices && input.devices.length ? (
          <li>
            <Trans>Devices</Trans>:{" "}
            <span className="font-medium">
              {input.devices
                .map((device) =>
                  i18n._(
                    DeviceCategoryLabels[
                      device as keyof typeof DeviceCategoryLabels
                    ],
                  ),
                )
                .join(", ")}
            </span>
          </li>
        ) : null}
        {input.os && input.os.length ? (
          <li>
            <Trans>OS</Trans>:{" "}
            <span className="font-medium">
              {input.os
                .map((os) =>
                  i18n._(OSCategoryLabels[os as keyof typeof OSCategoryLabels]),
                )
                .join(", ")}
            </span>
          </li>
        ) : null}
        {input.browsers && input.browsers.length ? (
          <li>
            <Trans>Browsers</Trans>:{" "}
            <span className="font-medium">
              {input.browsers
                .map((browser) =>
                  i18n._(
                    BrowserCategoryLabels[
                      browser as keyof typeof BrowserCategoryLabels
                    ],
                  ),
                )
                .join(", ")}
            </span>
          </li>
        ) : null}
        {input.sources && input.sources.length ? (
          <li>
            <Trans>Sources</Trans>:{" "}
            <span className="font-medium">{input.sources.join(", ")}</span>
          </li>
        ) : null}
        {input.mediums && input.mediums.length ? (
          <li>
            <Trans>Mediums</Trans>:{" "}
            <span className="font-medium">{input.mediums.join(", ")}</span>
          </li>
        ) : null}
        {input.terms && input.terms.length ? (
          <li>
            <Trans>Term</Trans>:{" "}
            <span className="font-medium">{input.terms.join(", ")}</span>
          </li>
        ) : null}
        {input.contents && input.contents.length ? (
          <li>
            <Trans>Content</Trans>:{" "}
            <span className="font-medium">{input.contents.join(", ")}</span>
          </li>
        ) : null}
        {campaignsSummary.length ? (
          <li>
            <Trans>Campaigns</Trans>:{" "}
            <span className="font-medium">{campaignsSummary.join(", ")}</span>
          </li>
        ) : null}
        {input.defaultChannelGroup && input.defaultChannelGroup.length ? (
          <li>
            <Trans>Default channel group</Trans>:{" "}
            <span className="font-medium">
              {input.defaultChannelGroup
                .map((channel) =>
                  i18n._(
                    DefaultChannelGroupLabels[
                      channel as keyof typeof DefaultChannelGroupLabels
                    ],
                  ),
                )
                .join(", ")}
            </span>
          </li>
        ) : null}
        {input.events && input.events.length ? (
          <li>
            <Trans>Events (include)</Trans>:{" "}
            <span className="font-medium">{input.events.join(", ")}</span>
          </li>
        ) : null}
        {input.eventsNotIn && input.eventsNotIn.length ? (
          <li>
            <Trans>Events (exclude)</Trans>:{" "}
            <span className="font-medium">{input.eventsNotIn.join(", ")}</span>
          </li>
        ) : null}
        {eventParamsSummary.length ? (
          <li>
            <Trans>Event parameters</Trans>:{" "}
            <span className="font-medium">{eventParamsSummary.join(", ")}</span>
          </li>
        ) : null}
        {input.expVariantString && input.expVariantString.length ? (
          <li>
            <Trans>AB Testing</Trans>:{" "}
            <span className="font-medium">
              {input.expVariantString.join(", ")}
            </span>
          </li>
        ) : null}
        {typeof input.isSessionWithNavigation === "boolean" ? (
          <li>
            <Trans>Sessions with surf activity</Trans>:{" "}
            <span className="font-medium">
              {input.isSessionWithNavigation ? (
                <Trans>Yes</Trans>
              ) : (
                <Trans>No</Trans>
              )}
            </span>
          </li>
        ) : null}
        {input.pageLocationCount &&
        (input.pageLocationCount.min != null ||
          input.pageLocationCount.max != null) ? (
          <li>
            <Trans>Page location count</Trans>:{" "}
            <span className="font-medium">
              {input.pageLocationCount.min != null
                ? input.pageLocationCount.min
                : "—"}{" "}
              -{" "}
              {input.pageLocationCount.max != null
                ? input.pageLocationCount.max
                : "—"}
            </span>
          </li>
        ) : null}
        {input.gaSessionNumber &&
        (input.gaSessionNumber.min != null ||
          input.gaSessionNumber.max != null) ? (
          <li>
            <Trans>Session Number</Trans>:{" "}
            <span className="font-medium">
              {input.gaSessionNumber.min != null
                ? input.gaSessionNumber.min
                : "—"}{" "}
              -{" "}
              {input.gaSessionNumber.max != null
                ? input.gaSessionNumber.max
                : "—"}
            </span>
          </li>
        ) : null}
        {detailGroupLabels.length ? (
          <li>
            <Trans>Group by</Trans>:{" "}
            <span className="font-medium">{detailGroupLabels.join(", ")}</span>
          </li>
        ) : null}
        {detailOrderByLabel ? (
          <li>
            <Trans>Order by</Trans>:{" "}
            <span className="font-medium">
              {detailOrderByLabel}
              {detailOrderDirectionLabel ? (
                <span className="ml-2 text-muted-foreground">
                  {detailOrderDirectionLabel}
                </span>
              ) : null}
            </span>
          </li>
        ) : null}
      </ul>
      <div className="flex gap-2">
        <ApproveButton
          toolCallId={part.toolCallId}
          toolName={getToolName(part)}
          addToolResult={addToolResult}
          sendMessage={sendMessage}
        >
          <Trans>Create</Trans>
        </ApproveButton>
        <DenyButton
          toolCallId={part.toolCallId}
          toolName={getToolName(part)}
          addToolResult={addToolResult}
          sendMessage={sendMessage}
        >
          <Trans>Cancel</Trans>
        </DenyButton>
      </div>
    </div>
  );
}
